import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, fmtDateTime, titleCase, StatusBadge } from "@/lib/format";
import { FormDialog } from "@/components/EntityForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Phone, Mail, Loader2, Car, ShieldCheck, FileSpreadsheet,
  Wallet, Users, FileText, Plus, Send, Upload, Download, Trash2, FolderLock, Landmark,
} from "lucide-react";
import { API } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useCallTracker } from "@/components/CallTracker";

function DocumentsTab({ customerId, docs, canAdd, canDelete, reload }) {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("KYC");
  const CATS = ["KYC", "Client", "Vehicle", "Proposal", "Quote", "Policy", "Payment", "Claim", "Renewal", "Service", "Other"];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("customer_id", customerId);
    fd.append("category", category);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.original_filename}?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      {canAdd && (
        <div className="flex flex-wrap items-center gap-3 mb-4 rounded-lg border border-border bg-muted/30 p-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40" data-testid="document-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <label>
            <input type="file" className="hidden" onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.webp,.csv,.txt,.doc,.docx" data-testid="document-upload-input" />
            <Button asChild size="sm" disabled={uploading}><span className="cursor-pointer" data-testid="document-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" />Upload Document</>}
            </span></Button>
          </label>
          <span className="text-xs text-muted-foreground">PDF / image / doc · max 15 MB · secured & access-logged</span>
        </div>
      )}
      {!docs?.length ? <p className="text-sm text-muted-foreground py-6 text-center">No documents uploaded</p> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-border p-3 text-sm flex items-center gap-3" data-testid="document-row">
              <FileText className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{d.original_filename}</div>
                <div className="text-xs text-muted-foreground">{d.category} · {(d.size / 1024).toFixed(0)} KB · {fmtDate(d.created_at)}</div>
              </div>
              <StatusBadge value={d.verification_status} />
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => window.open(`${API}/documents/${d.id}/download`, "_blank")} title="Download" data-testid="document-download-btn"><Download className="h-4 w-4" /></Button>
              {canDelete && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => del(d)} data-testid="document-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (<div className="flex justify-between py-2 border-b border-border/60 text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium text-right">{value || "—"}</span></div>);
}

function MiniList({ items, cols, empty }) {
  if (!items?.length) return <p className="text-sm text-muted-foreground py-6 text-center">{empty}</p>;
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.id || i} className="rounded-lg border border-border p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          {cols(it)}
        </div>
      ))}
    </div>
  );
}

export default function CustomerDetail() {
  const { trackCall } = useCallTracker();
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/customers/${id}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false)); };
  useEffect(load, [id]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Customer not found</div>;
  const c = d.customer;

  const addVehicle = async (v) => { await api.post(`/customers/${id}/vehicles`, v); load(); };
  const addFamily = async (v) => { await api.post(`/customers/${id}/family`, v); load(); };
  const sendEmail = async (event) => {
    try { await api.post("/communications/email", { customer_id: id, event }); toast.success("Email sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Email failed"); }
  };

  const vehicleFields = [
    { name: "registration_number", label: "Registration No", required: true, placeholder: "MH-02-AB-1234" },
    { name: "make", label: "Make" }, { name: "model", label: "Model" },
    { name: "manufacturing_year", label: "Mfg Year", type: "number" },
    { name: "fuel_type", label: "Fuel Type", type: "select", options: ["Petrol", "Diesel", "CNG", "Electric"] },
    { name: "vehicle_type", label: "Vehicle Type", type: "select", options: ["Private Car", "Two Wheeler", "Commercial"] },
    { name: "engine_number", label: "Engine Number" }, { name: "chassis_number", label: "Chassis Number" },
    { name: "idv", label: "IDV", type: "number" }, { name: "ncb", label: "NCB %", type: "number" },
  ];
  const familyFields = [
    { name: "name", label: "Name", required: true }, { name: "relationship", label: "Relationship" },
    { name: "dob", label: "DOB", type: "date" }, { name: "contact", label: "Contact" },
  ];

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => nav("/customers")} className="mb-4" data-testid="back-to-customers"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Customers</Button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left profile card */}
        <Card className="p-6 h-fit lg:sticky lg:top-24">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-2xl font-extrabold font-heading">{c.name?.charAt(0)}</div>
            <div><h1 className="text-xl font-extrabold">{c.name}</h1><p className="font-mono text-xs text-muted-foreground">{c.customer_id}</p></div>
          </div>
          <div className="flex gap-2 mb-5">
            <Button size="sm" className="flex-1" asChild data-testid="customer-call-btn"><a href={`tel:${c.primary_mobile}`} onClick={() => trackCall({ customer_id: c.id, context: "customer", name: c.name, mobile: c.primary_mobile })}><Phone className="h-4 w-4" /></a></Button>
            <WhatsAppButton lead={{ name: c.name, mobile: c.primary_mobile }} size="sm" variant="outline" className="flex-1" testId="customer-whatsapp-btn" />
            {hasPerm("communications:add") && <Button size="sm" variant="outline" className="flex-1" onClick={() => sendEmail("welcome")} data-testid="customer-email-btn"><Mail className="h-4 w-4" /></Button>}
          </div>
          <div className="flex gap-2 mb-4">
            <StatusBadge value={c.segment} /><StatusBadge value={c.status} />
          </div>
          <InfoRow label="Primary Mobile" value={c.primary_mobile} />
          <InfoRow label="Alternate" value={c.alternate_mobile} />
          <InfoRow label="Email" value={c.email} />
          <InfoRow label="Occupation" value={c.occupation} />
          <InfoRow label="Location" value={[c.city, c.state].filter(Boolean).join(", ")} />
          <InfoRow label="Pincode" value={c.pincode} />
          <div className="grid grid-cols-3 gap-2 mt-5 text-center">
            <div className="rounded-lg bg-muted/50 p-2"><div className="text-lg font-extrabold tabular">{d.clv.total_policies}</div><div className="text-[10px] text-muted-foreground uppercase">Policies</div></div>
            <div className="rounded-lg bg-muted/50 p-2"><div className="text-lg font-extrabold tabular">{d.clv.active_policies}</div><div className="text-[10px] text-muted-foreground uppercase">Active</div></div>
            <div className="rounded-lg bg-muted/50 p-2"><div className="text-sm font-extrabold tabular">{INR(d.clv.total_premium)}</div><div className="text-[10px] text-muted-foreground uppercase">Premium</div></div>
          </div>
        </Card>

        {/* Right tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="policies">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="policies" data-testid="customer-360-tab-policies"><ShieldCheck className="h-4 w-4 mr-1.5" />Policies ({d.policies.length})</TabsTrigger>
              <TabsTrigger value="vehicles" data-testid="customer-360-tab-vehicles"><Car className="h-4 w-4 mr-1.5" />Vehicles ({d.vehicles.length})</TabsTrigger>
              <TabsTrigger value="quotes"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Quotes ({d.quotes.length})</TabsTrigger>
              <TabsTrigger value="payments"><Wallet className="h-4 w-4 mr-1.5" />Payments ({d.payments.length})</TabsTrigger>
              <TabsTrigger value="family"><Users className="h-4 w-4 mr-1.5" />Family ({c.family_members?.length || 0})</TabsTrigger>
              <TabsTrigger value="documents" data-testid="customer-360-tab-documents"><FolderLock className="h-4 w-4 mr-1.5" />Documents ({d.documents.length})</TabsTrigger>
              <TabsTrigger value="loans" data-testid="customer-360-tab-loans"><Landmark className="h-4 w-4 mr-1.5" />Loans ({(d.loan_applications?.length || 0) + (d.loan_leads?.length || 0)})</TabsTrigger>
              <TabsTrigger value="activity"><FileText className="h-4 w-4 mr-1.5" />Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="policies" className="mt-4">
              <MiniList items={d.policies} empty="No policies yet" cols={(p) => (<>
                <span className="font-mono text-xs">{p.policy_number}</span>
                <span className="font-semibold">{p.insurer}</span>
                <span className="text-muted-foreground">{p.product}</span>
                <span className="ml-auto tabular font-semibold">{INR(p.premium)}</span>
                <StatusBadge value={p.policy_status} />
                <span className="text-xs text-muted-foreground w-full">Expiry: {fmtDate(p.expiry_date)}{p.previous_policy_number && ` · Renewal of ${p.previous_policy_number}`}</span>
              </>)} />
            </TabsContent>

            <TabsContent value="vehicles" className="mt-4">
              {hasPerm("customers:edit") && <div className="mb-3"><FormDialog title="Add Vehicle" fields={vehicleFields} onSubmit={addVehicle} trigger={<Button size="sm" data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-1.5" />Add Vehicle</Button>} /></div>}
              <MiniList items={d.vehicles} empty="No vehicles added" cols={(v) => (<>
                <span className="font-mono text-xs font-semibold">{v.registration_number}</span>
                <span>{v.make} {v.model} {v.manufacturing_year ? `(${v.manufacturing_year})` : ""}</span>
                <span className="text-muted-foreground">{v.fuel_type}</span>
                <span className="ml-auto text-xs text-muted-foreground">IDV {INR(v.idv)} · NCB {v.ncb || 0}%</span>
              </>)} />
            </TabsContent>

            <TabsContent value="quotes" className="mt-4">
              <MiniList items={d.quotes} empty="No quotes generated" cols={(q) => (<>
                <span className="font-mono text-xs">{q.quote_number}</span><span className="font-semibold">{q.insurer}</span>
                <span className="ml-auto tabular">{INR(q.premium)}</span><StatusBadge value={q.status} />
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted">{titleCase(q.quote_source || "manual")}</span>
              </>)} />
            </TabsContent>

            <TabsContent value="payments" className="mt-4">
              <MiniList items={d.payments} empty="No payments recorded" cols={(p) => (<>
                <span className="font-mono text-xs">{p.receipt_number}</span>
                <span className="ml-auto tabular font-semibold">{INR(p.amount_received)}</span>
                <span className="text-xs text-muted-foreground">of {INR(p.policy_booked_amount)}</span>
                <StatusBadge value={p.payment_status} /><span className="text-xs text-muted-foreground w-full">{fmtDate(p.payment_date)} · {p.payment_mode}</span>
              </>)} />
            </TabsContent>

            <TabsContent value="family" className="mt-4">
              {hasPerm("customers:edit") && <div className="mb-3"><FormDialog title="Add Family Member" fields={familyFields} onSubmit={addFamily} trigger={<Button size="sm" data-testid="add-family-btn"><Plus className="h-4 w-4 mr-1.5" />Add Member</Button>} /></div>}
              <MiniList items={c.family_members} empty="No family members" cols={(f) => (<>
                <span className="font-semibold">{f.name}</span><span className="text-muted-foreground">{titleCase(f.relationship)}</span>
                <span className="ml-auto text-xs text-muted-foreground">{fmtDate(f.dob)}</span></>)} />
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <DocumentsTab customerId={id} docs={d.documents} canAdd={hasPerm("documents:add")} canDelete={hasPerm("documents:delete")} reload={load} />
            </TabsContent>

            <TabsContent value="loans" className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Loan Applications</div>
              <MiniList items={d.loan_applications} empty="No loan applications" cols={(a) => (<>
                <span className="font-mono text-xs">{a.loan_app_id}</span>
                <span className="font-semibold">{a.loan_product}</span>
                <span className="text-muted-foreground">{a.lender}</span>
                <span className="ml-auto tabular">{INR(a.requested_amount)}</span>
                <StatusBadge value={a.status} />
              </>)} />
              <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Loan Leads</div>
              <MiniList items={d.loan_leads} empty="No loan leads" cols={(l) => (<>
                <span className="font-mono text-xs">{l.loan_lead_id}</span>
                <span className="font-semibold">{l.loan_type}</span>
                <span className="ml-auto tabular">{INR(l.required_amount)}</span>
                <StatusBadge value={l.status} />
              </>)} />
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <MiniList items={d.communications} empty="No communication history" cols={(a) => (<>
                <StatusBadge value={a.channel} /><span className="font-medium">{titleCase(a.event || a.subject)}</span>
                <StatusBadge value={a.status} /><span className="ml-auto text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span></>)} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
